import type { RangeFilterValue } from '../../lib/dataTable/filters.js';
import { useI18n } from '../../i18n/I18nContext.js';

/**
 * The small set of per-column filter controls every table composes with
 * `useColumnFilters`. Each one is a plain controlled input — the table page
 * owns the actual filter state via the hook, these just render it and
 * advertise the expected format, per the admin house rules (money shows a
 * "$" mask, percentages a "%" suffix, every input's format is visible before
 * the admin types).
 */

export interface TextFilterInputProps {
  id: string;
  /** Column label, e.g. "Nombre" — used to build both the visible placeholder and the accessible name. */
  column: string;
  value: string;
  onChange(value: string): void;
}

export function TextFilterInput({ id, column, value, onChange }: TextFilterInputProps) {
  const { t } = useI18n();
  return (
    <input
      id={id}
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={t('table.filterContainsPlaceholder')}
      aria-label={t('table.filterBy', { column })}
    />
  );
}

export interface SelectFilterOption {
  value: string;
  label: string;
}

export interface SelectFilterInputProps {
  id: string;
  column: string;
  value: string;
  onChange(value: string): void;
  options: SelectFilterOption[];
  /** Label for the "no filter" option, defaults to "Todos". */
  allLabel?: string;
}

export function SelectFilterInput({ id, column, value, onChange, options, allLabel }: SelectFilterInputProps) {
  const { t } = useI18n();
  return (
    <select id={id} value={value} onChange={(e) => onChange(e.target.value)} aria-label={t('table.filterBy', { column })}>
      <option value="">{allLabel ?? t('common.all')}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

export interface RangeFilterInputProps {
  id: string;
  column: string;
  value: RangeFilterValue;
  onChange(value: RangeFilterValue): void;
  /** A decorative affix mask — "$" for money, "%" for percentages — matching `MoneyInput`/`PercentInput`. Omitted for plain quantities. */
  affix?: { symbol: string; position: 'prefix' | 'suffix' };
  /** Shown inside each bound as the expected shape, e.g. "0.00" for money, "0" for a plain quantity. */
  placeholder: string;
}

/**
 * Two bounds (min/max), either one optional — an admin filtering "price over
 * $50" only fills in the min. Money and percent variants reuse the same
 * `.input-affix` mask the rest of the app already uses for those field
 * kinds, so a filter input looks and behaves exactly like the form field it
 * filters.
 */
export function RangeFilterInput({ id, column, value, onChange, affix, placeholder }: RangeFilterInputProps) {
  const { t } = useI18n();
  const wrapperClass = affix ? `input-affix input-affix--${affix.position}` : undefined;

  function bound(kind: 'min' | 'max') {
    const input = (
      <input
        id={`${id}-${kind}`}
        type="text"
        inputMode="decimal"
        className="numeric"
        value={value[kind]}
        placeholder={placeholder}
        aria-label={t(kind === 'min' ? 'table.filterMinLabel' : 'table.filterMaxLabel', { column })}
        onChange={(e) => onChange({ ...value, [kind]: e.target.value })}
      />
    );
    if (!affix) return input;
    return (
      <span className={wrapperClass}>
        {affix.position === 'prefix' && (
          <span className="input-affix__symbol" aria-hidden="true">
            {affix.symbol}
          </span>
        )}
        {input}
        {affix.position === 'suffix' && (
          <span className="input-affix__symbol" aria-hidden="true">
            {affix.symbol}
          </span>
        )}
      </span>
    );
  }

  return (
    <div className="table-filter-range">
      {bound('min')}
      <span aria-hidden="true" className="table-filter-range__sep">
        –
      </span>
      {bound('max')}
    </div>
  );
}

export interface FilterSummaryProps {
  activeCount: number;
  onClear(): void;
}

/** "N filters active" plus a clear control — shown once per table, not per column, so clearing is a single obvious action. */
export function FilterSummary({ activeCount, onClear }: FilterSummaryProps) {
  const { t } = useI18n();
  if (activeCount === 0) return null;
  return (
    <div className="toolbar table-filter-summary">
      <span className="badge badge--neutral">{t('table.filtersActive', { count: activeCount })}</span>
      <button type="button" className="btn btn--ghost btn--sm" onClick={onClear}>
        {t('table.clearFilters')}
      </button>
    </div>
  );
}
